import {
  useMemo,
  createContext,
  useState,
  useCallback,
  useContext,
} from "react";

interface Campaign {
  id?: string;
  name: string;
  playstoreUrl: string;
  biddingType: "roas_cpi";
  platform: "1" | "2";
  appStoreUrl: string;
  bid: number;
  dailyBudgetCap: number;
  countries: string[];
  genders: number[];
  minAge: number;
  maxAge: number;
  minApiLevel: number;
  maxApiLevel: number;
  clickUrl: string;
  iconUrl: string;
  title: string;
  mmp: "appsflyer" | "adjust" | "singular" | "tenjin";
}

export interface RoasGoal {
  D0: string;
  D7: string;
  D30: string;
  D90: string;
  D180: string;
  D365: string;
}

interface Creatives {
  iconUrl: string;
  bannerImageUrl?: string;
  bannerVideoUrl?: string;
}

interface CreativeFiles {
  bannerImage: File | null;
  bannerVideo: File | null;
}

interface ICreateCampaignContext {
  campaign?: Campaign;
  roasGoals?: RoasGoal;
  creatives?: Creatives;
  creativeFiles?: CreativeFiles;
  handleUpdateCampaignConfiguration: (data: Partial<Campaign>) => void;
  handleUpdateRoasGoals: (data: Partial<RoasGoal>) => void;
  handleUpdateCreatives: (data: Partial<Creatives>) => void;
  handleUpdateCreativeFiles: (data: Partial<CreativeFiles>) => void;
  handleReset: () => void;
}

const CampaignCreateContext = createContext<ICreateCampaignContext>({
  campaign: null,
  roasGoals: {
    D0: "",
    D7: "",
    D30: "",
    D90: "",
    D180: "",
    D365: "",
  },
  creatives: {
    iconUrl: "",
    bannerImageUrl: "",
    bannerVideoUrl: "",
  },
  creativeFiles: {
    bannerImage: null,
    bannerVideo: null,
  },
  handleUpdateCampaignConfiguration: () => {},
  handleUpdateRoasGoals: () => {},
  handleUpdateCreatives: () => {},
  handleUpdateCreativeFiles: () => {},
  handleReset: () => {},
});

const ProvideCampaignCreate = (): ICreateCampaignContext => {
  const [roasGoals, setRoasGoals] = useState<RoasGoal>({
    D0: "",
    D7: "",
    D30: "",
    D90: "",
    D180: "",
    D365: "",
  });
  const [creatives, setCreatives] = useState<Creatives>({
    iconUrl: "",
    bannerImageUrl: "",
    bannerVideoUrl: "",
  });
  const [creativeFiles, setCreativeFiles] = useState<CreativeFiles>({
    bannerImage: null,
    bannerVideo: null,
  });
  const [campaign, setCampaign] = useState<Campaign | null>(null);

  const handleUpdateCampaignConfiguration = useCallback(
    (data: Partial<Campaign>) => {
      setCampaign((prev) => ({ ...prev, ...data }));
    },
    []
  );

  const handleUpdateRoasGoals = useCallback((data: Partial<RoasGoal>) => {
    setRoasGoals((prev) => ({ ...prev, ...data }));
  }, []);

  const handleUpdateCreatives = useCallback((data: Partial<Creatives>) => {
    setCreatives((prev) => ({ ...prev, ...data }));
  }, []);

  const handleUpdateCreativeFiles = useCallback(
    (data: Partial<CreativeFiles>) => {
      setCreativeFiles((prev) => ({ ...prev, ...data }));
    },
    []
  );

  const handleReset = useCallback(() => {
    setCampaign(null);
    setRoasGoals({
      D0: "",
      D7: "",
      D30: "",
      D90: "",
      D180: "",
      D365: "",
    });
    setCreatives({
      iconUrl: "",
      bannerImageUrl: "",
      bannerVideoUrl: "",
    });
    setCreativeFiles({
      bannerImage: null,
      bannerVideo: null,
    });
  }, []);

  return {
    campaign,
    handleUpdateCampaignConfiguration,
    handleUpdateRoasGoals,
    handleUpdateCreatives,
    handleUpdateCreativeFiles,
    handleReset,
    roasGoals,
    creatives,
    creativeFiles,
  };
};

const CampaignCreateProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const provideCampaignCreate = ProvideCampaignCreate();

  const memoizedValue = useMemo(
    () => provideCampaignCreate,
    [provideCampaignCreate]
  );

  return (
    <CampaignCreateContext.Provider value={memoizedValue}>
      {children}
    </CampaignCreateContext.Provider>
  );
};

const useCampaignCreate = () => {
  const context = useContext(CampaignCreateContext);
  if (context === undefined) {
    throw new Error(
      "useCampaignCreate must be used within a CampaignCreateProvider"
    );
  }
  return context;
};

export { CampaignCreateProvider, useCampaignCreate };
